import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { InMemoryDbService, RequestInfo, ResponseOptions, STATUS } from 'angular-in-memory-web-api';

import { OperationsFromServer } from '@core/services/operations/operations.model';

/**
 * Коды ошибок API (приходят в теле ответа с HTTP 200)
 */
export enum ApiErrorCode {
  USER_NOT_BLOCKED = 1005, // Пользователь не может быть разблокирован
  SERVICE_MODE = 1006, // Система в сервисном режиме
  INTERNAL_ERROR = 1007, // Внутренняя системная ошибка
  MISSING_PARAM = 1008, // Не указан обязательный параметр
  INVALID_FORMAT = 1009, // Неправильный формат данных
  DOC_TYPE_NOT_ALLOWED = 3000, // Документ не разрешен для пользователя
  DOC_NOT_FOUND = 3005, // Документ не найден
  DOC_NOT_PROVIDED = 3010, // Документ не предоставлен
  DOC_CREATION_FAILED = 3011 // Не удается создать документ
}

/**
 * Текстовые описания ошибок
 */
const ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  [ApiErrorCode.USER_NOT_BLOCKED]:
    'Пользователь не может быть разблокирован, т.к. он не заблокирован, либо заблокирован постоянной блокировкой',
  [ApiErrorCode.SERVICE_MODE]: 'Система находится в сервисном режиме работы',
  [ApiErrorCode.INTERNAL_ERROR]: 'Внутренняя системная ошибка при вызове веб сервиса',
  [ApiErrorCode.MISSING_PARAM]: 'Не указан один из обязательных параметров',
  [ApiErrorCode.INVALID_FORMAT]: 'Неправильный формат или значения входных данных веб сервиса',
  [ApiErrorCode.DOC_TYPE_NOT_ALLOWED]: 'Документы с указанными модулем и типом не разрешены для пользователя',
  [ApiErrorCode.DOC_NOT_FOUND]: 'Документ с указанными модулем, типом и идентификатором не найден',
  [ApiErrorCode.DOC_NOT_PROVIDED]: 'Документ не предоставлен',
  [ApiErrorCode.DOC_CREATION_FAILED]: 'Не удается создать документ с указанными модулем и типом'
};

/**
 * In-Memory API service для тестирования и разработки.
 * Предоставляет mock-эндпоинты для операций с документами.
 */
@Injectable({
  providedIn: 'root'
})
export class InMemoryDataService implements InMemoryDbService {
  private readonly sbpMockDocId = 'mock-sbp-doc-1';
  private readonly sbpMockDocModule = 'ibankfl';
  private readonly sbpMockDocType = 'request_sbp_connect_agree';

  private readonly sbpDocStatusById: Record<string, string> = {
    'mock-sbp-doc-1': 'send'
  };

  /**
   * Создает начальную базу данных для in-memory API
   */
  createDb(): Record<string, unknown> {
    // Mock данные для списка документов
    const lastDocuments = {
      lastDocument: [
        // ============================================
        // ПРИОРИТЕТ 1: doc_pay_transfer_rur со статусом SEND (можно отменять)
        // ============================================
        {
          id: '1035088',
          docModule: 'ibankfl',
          docType: 'doc_pay_transfer_rur',
          infoRef: { docDate: '2025-10-28T14:23:54.000', docNum: '101' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'Петров Иван Сергеевич',
              account: '40817810100120112345',
              inn: '784123456789',
              bankName: 'МОСКОВСКИЙ ФИЛИАЛ ПАО РОСБАНК, г.МОСКВА',
              bic: '044525256',
              corAccount: '30101810000000000256'
            },
            paymentInfo: { amount: '5000.00', currency: 'RUB', purpose: 'Возврат долга по договору займа' }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-28T14:24:01.000'
        },
        {
          id: '1035089',
          docModule: 'ibankfl',
          docType: 'doc_pay_transfer_rur',
          infoRef: { docDate: '2025-10-28T10:15:30.000', docNum: '102' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'Сидорова Анна Петровна',
              account: '40817810200120112345',
              inn: '773012345678',
              bankName: 'АО "АЛЬФА-БАНК", г.МОСКВА',
              bic: '044525593',
              corAccount: '30101810200000000593'
            },
            paymentInfo: { amount: '12500.00', currency: 'RUB', purpose: 'Оплата по договору оказания услуг №45' }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-28T10:16:00.000'
        },
        {
          id: '1035090',
          docModule: 'ibankfl',
          docType: 'doc_pay_transfer_rur',
          infoRef: { docDate: '2025-10-27T18:45:12.000', docNum: '103' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'ООО "Строймонтаж"',
              account: '40702810300120012345',
              inn: '7709876543',
              bankName: 'ВТБ (ПАО), г.МОСКВА',
              bic: '044525187',
              corAccount: '30101810700000000187'
            },
            paymentInfo: {
              amount: '75000.00',
              currency: 'RUB',
              purpose: 'Оплата за строительные материалы по счету №567'
            }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-27T18:45:45.000'
        },

        // ============================================
        // ПРИОРИТЕТ 2: doc_pay_transfer_rur с другими статусами (отменять НЕЛЬЗЯ)
        // ============================================
        {
          id: '1035091',
          docModule: 'ibankfl',
          docType: 'doc_pay_transfer_rur',
          infoRef: { docDate: '2025-10-26T12:30:00.000', docNum: '104' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'Иванов Петр Александрович',
              account: '40817810400120112345',
              inn: '771234567890',
              bankName: 'ПАО СБЕРБАНК, г.МОСКВА',
              bic: '044525225',
              corAccount: '30101810400000000225'
            },
            paymentInfo: { amount: '3000.00', currency: 'RUB', purpose: 'Перевод средств' }
          },
          status: { value: 'end', displayName: 'Исполнен' },
          statusDate: '2025-10-26T12:30:30.000'
        },
        {
          id: '1035092',
          docModule: 'ibankfl',
          docType: 'doc_pay_transfer_rur',
          infoRef: { docDate: '2025-10-25T09:20:30.000', docNum: '105' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'Смирнов Дмитрий Викторович',
              account: '40817810500120112345',
              inn: '772234567890',
              bankName: 'БАНК ВТБ (ПАО), г.МОСКВА',
              bic: '044525187',
              corAccount: '30101810700000000187'
            },
            paymentInfo: { amount: '8500.00', currency: 'RUB', purpose: 'Оплата аренды квартиры' }
          },
          status: { value: 'decline', displayName: 'Отклонен' },
          statusDate: '2025-10-25T09:21:00.000'
        },

        // ============================================
        // ПРИОРИТЕТ 3: Другие типы документов со статусом SEND (отменять НЕЛЬЗЯ)
        // ============================================
        {
          id: '1035093',
          docModule: 'ibankfl',
          docType: 'doc_service',
          infoRef: { docDate: '2025-10-27T14:00:00.000', docNum: '201' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'ООО "Мобильная связь"',
              account: '40702810100120012345',
              inn: '7712345678',
              bankName: 'ПАО СБЕРБАНК, г.МОСКВА',
              bic: '044525225',
              corAccount: '30101810400000000225'
            },
            paymentInfo: { amount: '850.00', currency: 'RUB', purpose: 'Оплата услуг связи' }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-27T14:00:30.000'
        },
        {
          id: '1035094',
          docModule: 'ibankfl',
          docType: 'best2pay_transfer',
          infoRef: { docDate: '2025-10-26T11:15:00.000', docNum: '202' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: { cardNumber: '5536 **** **** 1234' },
            paymentInfo: { amount: '2500.00', currency: 'RUB', purpose: 'Перевод на карту' }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-26T11:15:45.000'
        },
        {
          id: '1035095',
          docModule: 'ibankfl',
          docType: 'doc_sbp_transferbyqrcode',
          infoRef: { docDate: '2025-10-25T17:20:00.000', docNum: '203' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: { phone: '+7 905 987-65-43' },
            paymentInfo: { amount: '1200.00', currency: 'RUB', purpose: 'Оплата по QR-коду СБП' }
          },
          status: { value: 'send', displayName: 'Отправлен' },
          statusDate: '2025-10-25T17:20:15.000'
        },

        // ============================================
        // ПРИОРИТЕТ 4: Другие типы документов с другими статусами (отменять НЕЛЬЗЯ)
        // ============================================
        {
          id: '1035096',
          docModule: 'ibankfl',
          docType: 'doc_service',
          infoRef: { docDate: '2025-10-24T08:00:00.000', docNum: '204' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: {
              name: 'АО "ЖКХ Сервис"',
              account: '40702810500120012345',
              inn: '7709876543',
              bankName: 'ВТБ (ПАО), г.МОСКВА',
              bic: '044525187',
              corAccount: '30101810700000000187'
            },
            paymentInfo: { amount: '4500.00', currency: 'RUB', purpose: 'Оплата коммунальных услуг' }
          },
          status: { value: 'end', displayName: 'Исполнен' },
          statusDate: '2025-10-24T08:00:45.000'
        },
        {
          id: '1035097',
          docModule: 'ibankfl',
          docType: 'best2pay_transfer',
          infoRef: { docDate: '2025-10-23T13:45:00.000', docNum: '205' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: { cardNumber: '2202 **** **** 5678' },
            paymentInfo: { amount: '7000.00', currency: 'RUB', purpose: 'Перевод на карту МИР' }
          },
          status: { value: 'decline', displayName: 'Отклонен' },
          statusDate: '2025-10-23T13:45:30.000'
        },
        {
          id: '1035098',
          docModule: 'ibankfl',
          docType: 'doc_sbp_transferbyqrcode',
          infoRef: { docDate: '2025-10-22T10:30:00.000', docNum: '206' },
          paymentGroups: {
            payer: { account: '40817810700124112321', clientName: 'Харитонова Виктория Константиновна' },
            receiver: { phone: '+7 903 111-22-33' },
            paymentInfo: { amount: '3500.00', currency: 'RUB', purpose: 'Перевод через СБП' }
          },
          status: { value: 'end', displayName: 'Исполнен' },
          statusDate: '2025-10-22T10:30:25.000'
        },
        {
          id: '1035099',
          docModule: 'ibankfl',
          docType: 'request_open_contr',
          infoRef: { docDate: '2025-10-21T15:00:00.000', docNum: '207' },
          status: { value: 'decline', displayName: 'Отклонен' },
          statusDate: '2025-10-21T15:00:30.000'
        }
      ]
    };

    return { lastDocuments };
  }

  /**
   * Явная обработка GET-запросов для сценария подтверждения СБП.
   */
  get(reqInfo: RequestInfo): Observable<ResponseOptions> | undefined {
    const { url } = reqInfo;

    if (url.includes('/rest/personal/document/confirmation/request')) {
      return reqInfo.utils.createResponse$(() => ({
        body: {
          text: 'Введите код подтверждения',
          type: 'NOTIFY'
        },
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    if (url.includes('/rest/personal/document/byid')) {
      return reqInfo.utils.createResponse$(() => {
        const params = this.getQueryParams(url);
        const docId = params['doc_ids'] || this.sbpMockDocId;
        const docModule = params['doc_module'] || this.sbpMockDocModule;
        const docType = params['doc_type'] || this.sbpMockDocType;

        const status = this.sbpDocStatusById[docId] || 'end';

        return {
          body: {
            [docId]: {
              id: docId,
              docModule,
              docType,
              status,
              declineInfo: ''
            }
          },
          headers: reqInfo.headers,
          status: STATUS.OK,
          url: reqInfo.url
        };
      });
    }

    return undefined;
  }

  /**
   * Явная обработка PUT-запросов для сценария подтверждения СБП.
   */
  put(reqInfo: RequestInfo): Observable<ResponseOptions> | undefined {
    const { url } = reqInfo;

    if (url.includes('/rest/personal/document/confirmation/check/v2')) {
      return reqInfo.utils.createResponse$(() => {
        const body = reqInfo.utils.getJsonBody(reqInfo.req) as { docId?: string };
        const docId = body?.docId || this.sbpMockDocId;

        this.sbpDocStatusById[docId] = 'end';

        return {
          body: {
            message: 'Document is sent!',
            success: 'true'
          },
          headers: reqInfo.headers,
          status: STATUS.OK,
          url: reqInfo.url
        };
      });
    }

    if (url.includes('/rest/personal/document/confirmation/request/v2')) {
      return reqInfo.utils.createResponse$(() => ({
        body: {
          text: 'Введите код подтверждения',
          batchId: 'mock-sbp-batch-1',
          type: 'NOTIFY'
        },
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    return undefined;
  }

  /**
   * Явная обработка POST-запросов для перехвата абсолютных URL до отправки в реальный backend
   */
  post(reqInfo: RequestInfo): Observable<ResponseOptions> | undefined {
    const { url } = reqInfo;

    // DBOFL-6801: временный mock endpoint предрасчёта комиссии (iSimple).
    if (url.includes('/rest/personal/document/create/selffree/v2/info/commission')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) as { exchangeAmount?: string | number };
      const amount = Number(body?.exchangeAmount) || 0;

      return reqInfo.utils.createResponse$(() => ({
        body: {
          amount: amount >= 100 ? '15.00' : '0.00',
          commissionInfo: amount >= 100 ? '15.00 руб.' : '0.00 руб.',
          success: 'true'
        },
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    // Обработка включения СБП в сценарии RM-30869
    if (url.includes('/rest/personal/sbp/connect_agree/set')) {
      this.sbpDocStatusById[this.sbpMockDocId] = 'send';

      return reqInfo.utils.createResponse$(() => ({
        body: {
          id: this.sbpMockDocId,
          docModule: this.sbpMockDocModule,
          docType: this.sbpMockDocType
        },
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    // Обработка отмены документа
    if (url.includes('/rest/personal/cancel')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) as {
        cancelDocId: number;
        cancelReason: string;
      };

      // Валидация обязательных параметров
      if (!body?.cancelDocId || !body?.cancelReason) {
        return reqInfo.utils.createResponse$(() => this.createApiErrorResponse(reqInfo, ApiErrorCode.MISSING_PARAM));
      }

      // Проверка на симуляцию ошибок для тестирования
      const errorCode = this.shouldSimulateError(body);

      if (errorCode) {
        return reqInfo.utils.createResponse$(() => this.createApiErrorResponse(reqInfo, errorCode));
      }

      // Успешный ответ
      return reqInfo.utils.createResponse$(() => ({
        body: {
          id: `cancel_${body.cancelDocId}`,
          docModule: 'ibankfl',
          docType: 'request_cancel'
        },
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    // Обработка получения списка документов
    if (url.includes('/rest/personal/last_documents/doc_types')) {
      const db = this.createDb() as { lastDocuments: unknown };

      return reqInfo.utils.createResponse$(() => ({
        body: db.lastDocuments,
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    // Обработка статистики PFM
    if (url.includes('/rest/personal/pfm/stat')) {
      const mock = this.buildMockPFMStat();

      return reqInfo.utils.createResponse$(() => ({
        body: mock,
        headers: reqInfo.headers,
        status: STATUS.OK,
        url: reqInfo.url
      }));
    }

    // Вернуть undefined — пусть обработка пойдет по дефолту (и при passThruUnknownUrl=true уйдет на реальный backend)
    return undefined;
  }

  /**
   * Перехватывает HTTP запросы и возвращает mock-ответы
   */
  responseInterceptor(
    response: ResponseOptions,
    requestInfo: RequestInfo
  ): ResponseOptions | Observable<ResponseOptions> {
    const { method, url } = requestInfo;

    // Обработка запроса PFM-флага: включаем PFM для тестирования cancel flow
    if (method === 'get' && url.includes('/rest/personal/system/property') && url.includes('resource=SERVICES/PFM')) {
      response.body = { 'PFM/ENABLED': 1 } as unknown as Record<string, unknown>;
      response.status = STATUS.OK;

      return response;
    }

    // Обработка запроса на отмену документа
    if (method === 'post' && url.includes('/rest/personal/cancel')) {
      const body = requestInfo.utils.getJsonBody(requestInfo.req) as {
        cancelDocId: number;
        cancelReason: string;
      };

      // Проверка валидности данных
      if (!body.cancelDocId || !body.cancelReason) {
        return this.createApiErrorResponse(requestInfo, ApiErrorCode.MISSING_PARAM);
      }

      // Проверка на симуляцию ошибок для тестирования
      const errorCode = this.shouldSimulateError(body);

      if (errorCode) {
        return this.createApiErrorResponse(requestInfo, errorCode);
      }

      // Успешный ответ
      response.body = {
        id: `cancel_${body.cancelDocId}`,
        docModule: 'ibankfl',
        docType: 'request_cancel'
      };
      response.status = STATUS.OK;

      return response;
    }

    // Обработка запроса на получение списка документов
    if ((method === 'post' || method === 'get') && url.includes('/rest/personal/last_documents/doc_types')) {
      // Возвращаем данные из базы
      const db = this.createDb();

      response.body = db.lastDocuments;
      response.status = STATUS.OK;

      return response;
    }

    // Обработка статистики PFM
    if (method === 'post' && url.includes('/rest/personal/pfm/stat')) {
      response.body = this.buildMockPFMStat();
      response.status = STATUS.OK;

      return response;
    }

    // Для всех остальных запросов возвращаем стандартный ответ
    return response;
  }

  /**
   * Создает ответ с бизнес-ошибкой (HTTP 200 с errorCode в теле)
   */
  private createApiErrorResponse(
    reqInfo: RequestInfo,
    errorCode: ApiErrorCode,
    customMessage?: string
  ): ResponseOptions {
    return {
      body: {
        errorCode,
        errorText: customMessage || ERROR_MESSAGES[errorCode]
      },
      headers: reqInfo.headers,
      status: STATUS.OK, // ⚠️ Важно: бизнес-ошибки приходят с кодом 200
      url: reqInfo.url
    } as ResponseOptions;
  }

  /**
   * Создает ответ с HTTP ошибкой (4xx, 5xx)
   */
  private createHttpErrorResponse(requestInfo: RequestInfo, errorMessage: string, status: number): ResponseOptions {
    return {
      body: { error: errorMessage },
      headers: requestInfo.headers,
      status,
      statusText: STATUS[status] || 'Unknown Error',
      url: requestInfo.url
    } as ResponseOptions;
  }

  /**
   * Проверяет, нужно ли симулировать ошибку для тестирования
   * Используйте специальные значения в запросах для триггера ошибок:
   * - cancelDocId === 99999 → DOC_NOT_FOUND
   * - cancelDocId === 88888 → DOC_TYPE_NOT_ALLOWED
   * - cancelReason === 'ERROR_SERVICE_MODE' → SERVICE_MODE
   */
  private shouldSimulateError(body: { cancelDocId?: number; cancelReason?: string }): ApiErrorCode | null {
    // Тестирование: документ не найден
    if (body.cancelDocId === 99999) {
      return ApiErrorCode.DOC_NOT_FOUND;
    }

    // Тестирование: тип документа не разрешен
    if (body.cancelDocId === 88888) {
      return ApiErrorCode.DOC_TYPE_NOT_ALLOWED;
    }

    // Тестирование: система в сервисном режиме
    if (body.cancelReason === 'ERROR_SERVICE_MODE') {
      return ApiErrorCode.SERVICE_MODE;
    }

    // Тестирование: внутренняя ошибка
    if (body.cancelReason === 'ERROR_INTERNAL') {
      return ApiErrorCode.INTERNAL_ERROR;
    }

    return null;
  }

  /**
   * Извлекает query параметры из URL.
   */
  private getQueryParams(url: string): Record<string, string> {
    const params: Record<string, string> = {};

    try {
      const parsedUrl = new URL(url, 'http://localhost');

      parsedUrl.searchParams.forEach((value, key) => {
        params[key] = value;
      });
    } catch {
      return params;
    }

    return params;
  }

  /**
   * Статистика PFM — мок-ответ для POST /rest/personal/pfm/stat
   */
  private buildMockPFMStat(): OperationsFromServer {
    return {
      amountDebit: '5777.77',
      amountCredit: '0.00',
      statistic: [
        {
          category: { id: '1000006556', typeId: '2', caption: 'Операции в Интернет Банке' },
          amountDebit: '5777.77',
          items: {
            subCategory: { id: '1000011709', caption: 'Перевод клиенту банка' },
            amountDebit: '5777.77',
            transactions: [
              {
                id: '1000158336',
                tranDate: '2025-10-07T10:56:16.270',
                tranCode: '1684006',
                tranType: 'doc_pay_transfer_rur',
                tranStatus: '2',
                isTranDebit: 'true',
                amountRur: '44.44',
                amountCurr: '44.44',
                tranCurr: 'RUR',
                corrName: 'Д. Павел  Владимирович ',
                description: 'Перевод средств на текущие расходы, НДС не облагается',
                productId: '1661577',
                datasourceExtRef: 'isimple',
                statIgnore: 'false',
                docKind: 'payment'
              },
              {
                id: '1000161646',
                tranDate: '2025-10-27T17:38:04.147',
                tranCode: '1694824',
                tranType: 'doc_pay_transfer_rur',
                tranStatus: '2',
                isTranDebit: 'true',
                amountRur: '1.00',
                amountCurr: '1.00',
                tranCurr: 'RUR',
                corrName: 'И. Мария Олеговна',
                description: 'Перевод средств на текущие расходы, НДС не облагается',
                productId: '1646983',
                datasourceExtRef: 'isimple',
                statIgnore: 'false',
                docKind: 'payment'
              },
              {
                id: '1000161647',
                tranDate: '2025-10-27T17:38:24.561',
                tranCode: '1694828',
                tranType: 'doc_pay_transfer_rur',
                tranStatus: '0',
                isTranDebit: 'true',
                amountRur: '123.00',
                amountCurr: '123.00',
                tranCurr: 'RUR',
                corrName: 'И. Мария Олеговна',
                description: 'Перевод средств на текущие расходы, НДС не облагается',
                productId: '1644723',
                datasourceExtRef: 'isimple',
                statIgnore: 'false',
                docRef: '31510979314',
                docKind: 'payment'
              },
              {
                id: '1000162001',
                tranDate: '2025-10-28T16:10:00.000',
                tranCode: '1695001',
                tranType: 'doc_pay_transfer_rur',
                tranStatus: '1',
                isTranDebit: 'true',
                amountRur: '777.77',
                amountCurr: '777.77',
                tranCurr: 'RUR',
                corrName: 'Иванов Иван Иванович',
                description: 'Перевод средств на текущие расходы, НДС не облагается',
                productId: '1661577',
                datasourceExtRef: 'isimple',
                statIgnore: 'false',
                docKind: 'payment'
              },
              {
                id: '1000162002',
                tranDate: '2025-10-28T17:05:00.000',
                tranCode: '1695002',
                tranType: 'doc_pay_transfer_rur',
                tranStatus: '1',
                isTranDebit: 'true',
                amountRur: '5000.00',
                amountCurr: '5000.00',
                tranCurr: 'RUR',
                corrName: 'Петров Петр Петрович',
                description: 'Перевод средств на текущие расходы, НДС не облагается',
                productId: '1646983',
                datasourceExtRef: 'isimple',
                statIgnore: 'false',
                docKind: 'payment'
              }
            ]
          }
        }
      ]
    };
  }
}
